import { AnimatePresence, motion } from 'framer-motion';
import type { TimelineEntry, ToolActivity } from '../../lib/useRun.ts';
import { IconClose } from '../ui/Icons.tsx';

/**
 * The right rail answers "what is it doing right now, and what has it done" — Feature 15's
 * audit trail, live. During a 25s model call this is the only thing on screen proving the
 * agent is alive, so it stays populated even when the chat is quiet.
 */
export function WorkPanel({
  tools,
  timeline,
  onClose,
}: {
  tools: ToolActivity[];
  timeline: TimelineEntry[];
  onClose?: () => void;
}) {
  return (
    <aside className="lx-rail lx-scroll">
      {onClose && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--lx-s-2)' }}>
          <button className="lx-icon-btn lx-focus" onClick={onClose} aria-label="Close activity panel">
            <IconClose />
          </button>
        </div>
      )}

      <section>
        <h3 className="lx-eyebrow">Activity</h3>
        {tools.length === 0 ? (
          <p className="lx-muted" style={{ margin: 0, fontSize: 13 }}>Nothing running.</p>
        ) : (
          <AnimatePresence initial={false}>
            {tools.map((t) => (
              <motion.div
                key={t.callId}
                className="lx-activity"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.24 }}
              >
                <Dot state={t.ok === undefined ? 'run' : t.ok ? 'ok' : 'err'} />
                <code>{t.tool}</code>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </section>

      <section>
        <h3 className="lx-eyebrow">Run timeline</h3>
        {timeline.length === 0 ? (
          <p className="lx-muted" style={{ margin: 0, fontSize: 13 }}>No steps yet.</p>
        ) : (
          <ol className="lx-timeline">
            {timeline.map((e, i) => (
              <motion.li
                key={`${e.at}-${i}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.24 }}
              >
                <div style={{ fontSize: 13.5 }}>{e.label}</div>
                <div style={{ fontSize: 11.5, color: 'var(--lx-text-4)' }}>
                  {new Date(e.at).toLocaleTimeString()}
                </div>
              </motion.li>
            ))}
          </ol>
        )}
      </section>
    </aside>
  );
}

function Dot({ state }: { state: 'run' | 'ok' | 'err' }) {
  const color = state === 'ok' ? 'var(--lx-ok)' : state === 'err' ? 'var(--lx-err)' : 'var(--lx-amber)';
  return (
    <span className="lx-ping" style={{ width: 7, height: 7 }}>
      <i style={{ background: color }} />
      {state === 'run' && <em style={{ borderColor: color }} />}
    </span>
  );
}
